import React, { useRef, useState, useCallback } from "react";
import { ConnectKitButton } from "connectkit";
import { motion, useAnimation, AnimatePresence } from "framer-motion";
import { useAccount, useBalance } from "wagmi";
import { Loader2, Check, Wallet, Coins } from "lucide-react";
import confetti from 'canvas-confetti';
import { cn } from "../lib/utils";
import { formatEther } from "viem";

interface ConnectButtonProps {
  text?: {
    idle?: string;
    connecting?: string;
    connected?: string;
  };
  className?: string;
}

const WALLET_LOGOS = {
  metamask: "https://upload.wikimedia.org/wikipedia/commons/3/36/MetaMask_Fox.svg",
  walletconnect: "https://avatars.githubusercontent.com/u/37784886",
  coinbase: "https://avatars.githubusercontent.com/u/1885080",
  trustwallet: "https://trustwallet.com/assets/images/media/assets/trust_platform.svg",
  default: "https://avatars.githubusercontent.com/u/37784886",
};

export function ConnectButton({ 
  text = {
    idle: "Connect Wallet",
    connecting: "Connecting...",
    connected: "Connected"
  },
  className 
}: ConnectButtonProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [walletLogo, setWalletLogo] = useState<string | null>(null);
  const { address, isConnecting } = useAccount();
  const { data: balance } = useBalance({
    address,
    watch: true,
  });
  const controls = useAnimation();
  const [isHovered, setIsHovered] = useState(false);

  const triggerConfetti = useCallback(() => {
    const buttonRect = buttonRef.current?.getBoundingClientRect();
    if (!buttonRect) return;

    const x = (buttonRect.left + buttonRect.right) / 2 / window.innerWidth;
    const y = (buttonRect.top + buttonRect.bottom) / 2 / window.innerHeight;

    confetti({
      particleCount: 50,
      spread: 60,
      origin: { x, y },
      colors: ['#60A5FA', '#3B82F6', '#2563EB'],
      gravity: 1.2,
      scalar: 0.8,
      ticks: 80
    });
  }, []);

  const handleConnect = useCallback((provider?: string) => {
    const logo = provider?.toLowerCase().includes('metamask') 
      ? WALLET_LOGOS.metamask 
      : provider?.toLowerCase().includes('coinbase')
      ? WALLET_LOGOS.coinbase
      : provider?.toLowerCase().includes('trust')
      ? WALLET_LOGOS.trustwallet
      : WALLET_LOGOS.walletconnect;

    setWalletLogo(logo);
    controls.start({
      scale: [1, 0.95, 1.05, 1],
      transition: { duration: 0.4 }
    });
  }, [controls]);

  return (
    <ConnectKitButton.Custom>
      {({ isConnected, show, address: displayAddress, ensName, chain, connector }) => {
        const status = isConnecting ? "connecting" : isConnected ? "connected" : "idle";
        
        return (
          <motion.div 
            className="relative"
            animate={controls}
            onHoverStart={() => setIsHovered(true)}
            onHoverEnd={() => setIsHovered(false)}
          >
            <motion.button
              ref={buttonRef}
              onClick={() => {
                show();
                if (!isConnected) {
                  handleConnect(connector?.name);
                } else {
                  triggerConfetti();
                }
              }}
              className={cn(
                "relative overflow-hidden",
                "px-4 py-2 rounded-xl",
                "bg-gradient-to-br from-[#0054fa]/70 via-[#002787]/70 to-[#030a21]/70",
                "border border-blue-500/20",
                "shadow-lg shadow-blue-500/10",
                "hover:shadow-xl hover:shadow-blue-500/20",
                "hover:border-blue-500/30",
                "focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:ring-offset-2 focus:ring-offset-slate-950",
                "transition-all duration-300 ease-out",
                className
              )}
            >
              {/* Grid background */}
              <div className="absolute inset-0 bg-grid opacity-20" />

              {/* Glow effect on hover */}
              <AnimatePresence>
                {isHovered && (
                  <motion.div
                    className="absolute inset-0 bg-blue-500/10"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  />
                )}
              </AnimatePresence>

              {/* Shimmer effect */}
              <AnimatePresence>
                {isHovered && (
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent"
                    initial={{ x: "-100%" }}
                    animate={{ x: "100%" }}
                    exit={{ x: "100%" }}
                    transition={{ duration: 1, repeat: Infinity }}
                  />
                )}
              </AnimatePresence>

              {/* Content */}
              <div className="relative flex items-center justify-center gap-2">
                <motion.div
                  animate={status === "connecting" ? { rotate: 360 } : { rotate: 0 }}
                  transition={{ duration: 2, repeat: status === "connecting" ? Infinity : 0, ease: "linear" }}
                  className="text-blue-400"
                >
                  {status === "connecting" ? (
                    <Loader2 className="h-4 w-4" />
                  ) : status === "connected" ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <Wallet className="h-4 w-4" />
                  )}
                </motion.div>

                <motion.div
                  className="flex items-center gap-2 text-sm font-medium text-white"
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  key={status}
                >
                  <span>
                    {status === "idle" ? text.idle :
                     status === "connecting" ? text.connecting :
                     ensName || `${displayAddress?.slice(0, 6)}...${displayAddress?.slice(-4)}`}
                  </span>
                  
                  {/* Balance display */}
                  {status === "connected" && balance && (
                    <motion.div 
                      className="flex items-center gap-1 bg-blue-500/10 px-2 py-0.5 rounded-full border border-blue-500/20"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.2 }}
                    >
                      <Coins className="w-3 h-3 text-blue-400" />
                      <span className="text-blue-400">
                        {Number(formatEther(balance.value)).toFixed(2)} APE
                      </span>
                    </motion.div>
                  )}
                </motion.div>
              </div>
            </motion.button>

            {/* Wallet Logo Animation */}
            <AnimatePresence>
              {walletLogo && (
                <motion.div
                  initial={{ scale: 0, opacity: 0, y: 20 }}
                  animate={{ 
                    scale: [0, 1.2, 1],
                    opacity: [0, 1, 0],
                    y: [-20, 0, 20]
                  }}
                  exit={{ scale: 0, opacity: 0 }}
                  transition={{ duration: 0.8 }}
                  className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                >
                  <img 
                    src={walletLogo} 
                    alt="Wallet Logo"
                    className="w-12 h-12 object-contain"
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        );
      }}
    </ConnectKitButton.Custom>
  );
}